import {BigCalendar, BigCalendarEvent, BigCalendarPopupProps} from 'platform/big-calendar';
import {DataStatus} from 'platform/components';

import {useState} from 'react';

import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetLastActiveSellingBusinessCaseQuery, useUpdateTestDriveMutation} from '@dms/api/sales';
import {businessCaseRoutes} from '@dms/routes';
import {handleApiError, usePermissions} from '@dms/shared';

import {composePath, useNavigate, useRequiredParams} from 'shared/web';

import {CustomerDetailsPopup} from './CustomerDetailsPopup';
import {useBigCalendarControl} from './hooks/useBigCalendarControl';
import {TestDriveCalendarEvent} from './utils/transformTestDriveToCalendarEvent';

const formatToISOString = (date: Date): string => date.toISOString();

export function TestDrivesCalendar() {
  const {id: vehicleId} = useRequiredParams();
  const navigate = useNavigate();

  const [canCreateTestDrive, canEditTestDrive] = usePermissions({
    permissionKeys: ['createTestDrive', 'editTestDrive'],
  });

  const {data: lastActiveSellingBC} = useGetLastActiveSellingBusinessCaseQuery({
    vehicleId,
  });
  const {events, isLoading, isError, currentUser} = useBigCalendarControl();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [updateTestDrive] = useUpdateTestDriveMutation();

  const handleEventClick = (event: BigCalendarEvent) => {
    const testDriveEvent = event as TestDriveCalendarEvent;

    if (testDriveEvent.businessCaseId) {
      navigate(
        composePath(businessCaseRoutes.testDrive, {
          params: {id: testDriveEvent.businessCaseId},
        })
      );
    }
  };

  const handleNavigate = (date: Date) => {
    setCurrentDate(date);
  };

  const handleDateClick = () => {
    if (isNilOrEmpty(lastActiveSellingBC)) {
      return;
    }

    navigate(
      composePath(businessCaseRoutes.testDrive, {
        params: {id: lastActiveSellingBC?.id},
      })
    );
  };

  const handleEventDragMove = (event: BigCalendarEvent, newStart: Date, newEnd: Date) => {
    const testDriveEvent = event as TestDriveCalendarEvent;

    if (!testDriveEvent.testDriveData) {
      return Promise.reject(new Error('Missing test drive data'));
    }

    const updatePayload = {
      userId: testDriveEvent.testDriveData.userId,
      state: testDriveEvent.testDriveData.state,
      note: testDriveEvent.testDriveData.note,
      internalNote: testDriveEvent.testDriveData.internalNote,
      scheduledAt: formatToISOString(newStart),
      returnedAt: testDriveEvent.testDriveData.hasReturnedAt ? formatToISOString(newEnd) : null,
    };

    return updateTestDrive({
      testDriveId: testDriveEvent.id,
      updateTestDriveRequestBody: updatePayload,
    })
      .unwrap()
      .then(() => undefined)
      .catch(handleApiError);
  };

  const renderEventPopup = ({event, anchorElement, isVisible}: BigCalendarPopupProps) => {
    const testDriveEvent = event as TestDriveCalendarEvent;
    return (
      <CustomerDetailsPopup
        event={testDriveEvent}
        isVisible={isVisible}
        anchorElement={anchorElement}
      />
    );
  };

  return (
    <DataStatus data={currentUser ? events : null} isLoading={isLoading} isError={isError}>
      {({data}) => (
        <BigCalendar
          enableHover
          currentUser={currentUser!}
          events={data}
          allowedViews={['week', 'month']}
          defaultView="week"
          currentDate={currentDate}
          data-testid="test-drives-calendar"
          onEventClick={handleEventClick}
          onNavigate={handleNavigate}
          onEventDragMove={canEditTestDrive ? handleEventDragMove : undefined}
          onDateClick={canCreateTestDrive ? handleDateClick : undefined}
          renderEventPopup={renderEventPopup}
        />
      )}
    </DataStatus>
  );
}
